import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Moon, CalendarDays, Clock, Users, ChevronRight, Sun, Bell, X, Settings } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import { useSettings } from '../contexts/SettingsContext';
import { remindersAPI, groupsAPI, pushAPI } from '../utils/api';
import { formatGregorianDate, formatHijriDate, getRelativeTime, getRelativeTimeTranslated, adjustHijriDate, formatHijriDateDetailed, formatHijriFromStored, sortRemindersByNextOccurrence, formatGregorianDateShort, formatPersianFromStored, formatPersianDateShort, gregorianToJalali, getDayName } from '../utils/dateHelpers';
import BottomNav from '../components/BottomNav';
import { useNavigate } from 'react-router-dom';

const PUSH_DISMISSED_KEY = 'iremind_push_dismissed';
const HIJRI_AUTO_KEY = 'iremind_hijri_auto_v1';
const HIJRI_TOAST_KEY = 'iremind_hijri_toast_shown';

const HomePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const { settings } = useSettings();
  const [reminders, setReminders] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const today = new Date();

  // Push notification banner
  const [showPushBanner, setShowPushBanner] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  // Hijri auto-adjust toast
  const [showHijriToast, setShowHijriToast] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [rems, grps] = await Promise.all([
          remindersAPI.getAll(),
          groupsAPI.getAll(),
        ]);
        setReminders(Array.isArray(rems) ? rems : []);
        setGroups(Array.isArray(grps) ? grps : []);
      } catch (err) {
        console.error('Failed to load data:', err);
      } finally {
        setLoading(false);
      }
    };
    load();

    // ── Push banner: show once if not subscribed and not dismissed ────
    const checkPush = async () => {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
      if (localStorage.getItem(PUSH_DISMISSED_KEY)) return;
      try {
        const reg = await navigator.serviceWorker.getRegistration('/sw-push.js');
        const sub = reg ? await reg.pushManager.getSubscription() : null;
        if (!sub) setShowPushBanner(true);
      } catch { /* silent */ }
    };
    checkPush();

    // ── Hijri auto-adjust toast: show once after auto-adjust ──────────
    const hijriAutoSet = localStorage.getItem(HIJRI_AUTO_KEY);
    const hijriToastShown = localStorage.getItem(HIJRI_TOAST_KEY);
    if (hijriAutoSet && !hijriToastShown) {
      localStorage.setItem(HIJRI_TOAST_KEY, '1');
      setTimeout(() => {
        setShowHijriToast(true);
        setTimeout(() => setShowHijriToast(false), 5000);
      }, 2000);
    }
  }, []);

  // Subscribe to push notifications
  const handleEnablePush = async () => {
    setPushLoading(true);
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        localStorage.setItem(PUSH_DISMISSED_KEY, '1');
        setShowPushBanner(false);
        return;
      }
      const reg = await navigator.serviceWorker.register('/sw-push.js');
      await navigator.serviceWorker.ready;
      const { publicKey } = await pushAPI.getVapidKey();
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: publicKey,
      });
      await pushAPI.subscribe(sub.toJSON());
      setShowPushBanner(false);
    } catch (err) {
      console.error('Push subscribe error:', err);
      setShowPushBanner(false);
    } finally {
      setPushLoading(false);
    }
  };

  const dismissPushBanner = () => {
    localStorage.setItem(PUSH_DISMISSED_KEY, '1');
    setShowPushBanner(false);
  };

  const adjustedToday = adjustHijriDate(today, settings.hijriAdjustment);

  const sortedReminders = sortRemindersByNextOccurrence([...(Array.isArray(reminders) ? reminders : [])]);
  const displayedReminders = (Array.isArray(sortedReminders) ? sortedReminders : []).slice(0, 5);
  const displayedGroups = (Array.isArray(groups) ? groups : []).slice(0, 3);

  const getGroupName = (groupId) => {
    if (!groupId) return null;
    const group = groups.find(g => g.id === groupId);
    return group?.name || null;
  };

  if (loading) {
    return (
      <div className="w-full max-w-md mx-auto min-h-screen bg-[#F7F9FB] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="w-full max-w-md mx-auto min-h-screen bg-[#F7F9FB] relative flex flex-col">
      <div className="p-6 pb-24 flex-1">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {/* Greeting */}
          <div className="mb-6">
            <h1 className="text-3xl font-bold tracking-tight text-slate-900" data-testid="home-greeting" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {t('home.hello')}{user?.name ? `, ${user.name}` : ''}
            </h1>
            <p className="text-base text-slate-600 mt-1" style={{ fontFamily: 'Figtree, sans-serif' }}>
              {t('home.tagline')}
            </p>
          </div>

          {/* Calendar Display */}
          <div className="bg-white p-5 rounded-3xl shadow-[0_4px_20px_rgba(0,0,0,0.04)] border border-slate-50 mb-6" data-testid="dual-calendar-display">
            {/* Day name — shown once, small */}
            <div className="mb-3 pb-3 border-b border-slate-100">
              <p className="text-sm font-semibold text-slate-400 tracking-wide uppercase">
                {getDayName(today, lang)}
              </p>
            </div>

            {/* Calendar rows — dates prominent */}
            <div className="space-y-3">
              {/* Gregorian */}
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-blue-100 text-blue-700 rounded-lg flex items-center justify-center shrink-0">
                  <CalendarDays size={15} />
                </div>
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400 w-20 shrink-0">{t('home.gregorian')}</p>
                <p className="text-base font-bold text-slate-900 ml-auto" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {formatGregorianDateShort(today, lang)}
                </p>
              </div>

              {/* Hijri */}
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-emerald-100 text-emerald-700 rounded-lg flex items-center justify-center shrink-0">
                  <Moon size={15} />
                </div>
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400 w-20 shrink-0">{t('home.hijri')}</p>
                <div className="flex items-center gap-2 ml-auto">
                  {settings.hijriAdjustment !== 0 && (
                    <span className="text-xs font-semibold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded-full">
                      {settings.hijriAdjustment > 0 ? '+' : ''}{settings.hijriAdjustment}
                    </span>
                  )}
                  <p className="text-base font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {formatHijriDateDetailed(adjustedToday, lang)}
                  </p>
                </div>
              </div>

              {/* Persian — only if enabled */}
              {settings.persianCalendarEnabled && (
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-orange-100 text-orange-700 rounded-lg flex items-center justify-center shrink-0">
                    <Sun size={15} />
                  </div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-400 w-20 shrink-0">{t('home.persian') || 'PERSIAN'}</p>
                  <p className="text-base font-bold text-slate-900 ml-auto" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {(() => { const j = gregorianToJalali(today, lang); return `${j.day} ${j.monthName} ${j.year}`; })()}
                  </p>
                </div>
              )}
            </div>

          </div>

          {/* Discrete "more calendars" hint — outside the card, only when Persian is disabled */}
          {!settings.persianCalendarEnabled && (
            <button
              onClick={() => navigate('/settings')}
              className="flex items-center gap-1 -mt-4 mb-4 text-[10px] text-slate-400 hover:text-slate-500 transition-colors"
              data-testid="more-calendars-hint"
            >
              <Settings size={9} />
              <span>{t('home.persian_hint')}</span>
            </button>
          )}

          {/* Upcoming Reminders */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold tracking-tight text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <Clock size={20} className="text-slate-700" />
                {t('home.upcoming')}
              </h2>
              {reminders.length > 0 && (
                <button
                  onClick={() => navigate('/all-reminders')}
                  className="text-sm font-semibold text-blue-600 flex items-center gap-1"
                  data-testid="see-all-reminders"
                >
                  {t('home.see_all')}
                  <ChevronRight size={16} />
                </button>
              )}
            </div>
            {sortedReminders.length === 0 ? (
              <div className="bg-white p-8 rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.04)] text-center">
                <Clock className="mx-auto mb-3 text-slate-300" size={48} strokeWidth={1.5} />
                <p className="text-slate-500" style={{ fontFamily: 'Figtree, sans-serif' }}>{t('home.no_reminders')}</p>
                <button
                  onClick={() => navigate('/add')}
                  className="mt-4 text-slate-900 font-semibold text-sm"
                  data-testid="add-first-reminder"
                >
                  {t('home.add_first')}
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {displayedReminders.map((reminder) => {
                  const isHijri = reminder.calendarType === 'hijri';
                  const isPersian = reminder.calendarType === 'persian';
                  const freq = reminder.repeatFrequency || (reminder.repeatYearly ? 'yearly' : 'yearly');
                  const groupName = getGroupName(reminder.groupId);
                  const relTime = getRelativeTime(reminder.date, reminder.repeatYearly, settings.hijriAdjustment, reminder.hijriDate, reminder.calendarType, reminder.persianDate, freq);
                  const isToday = relTime === 'Today';
                  const isTomorrow = relTime === 'Tomorrow';

                  return (
                    <div
                      key={reminder.id}
                      data-testid="reminder-card-item"
                      onClick={() => navigate(`/reminder/${reminder.id}`)}
                      className="bg-white p-4 rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.04)] flex items-center gap-4 active:scale-[0.98] transition-all border border-transparent hover:border-slate-100 cursor-pointer"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-slate-900 font-semibold text-lg line-clamp-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            {reminder.title}
                          </h3>
                          <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${
                            isPersian ? 'bg-orange-100 text-orange-700' : isHijri ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'
                          }`}>
                            {isPersian ? <Sun size={12} /> : isHijri ? <Moon size={12} /> : <CalendarDays size={12} />}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                            reminder.category === 'birthday' ? 'bg-pink-100 text-pink-700'
                            : reminder.category === 'death' ? 'bg-slate-200 text-slate-600'
                            : 'bg-amber-100 text-amber-700'
                          }`}>{t(`reminder.${reminder.category || 'other'}`) || reminder.category}</span>
                          <p className="text-slate-500 text-sm" style={{ fontFamily: 'Figtree, sans-serif' }}>
                            {isPersian && reminder.persianDate
                              ? formatPersianFromStored(reminder.persianDate, lang)
                              : isHijri && reminder.hijriDate
                              ? formatHijriFromStored(reminder.hijriDate, lang)
                              : isHijri
                              ? formatHijriDateDetailed(new Date(reminder.date), lang)
                              : formatGregorianDateShort(new Date(reminder.date), lang)
                            }
                          </p>
                          {groupName && (
                            <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-semibold flex items-center gap-1">
                              <Users size={10} />
                              {groupName}
                            </span>
                          )}
                          {/* Frequency badge */}
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            freq === 'once'    ? 'bg-slate-100 text-slate-500'
                            : freq === 'weekly'  ? 'bg-amber-100 text-amber-700'
                            : freq === 'monthly' ? 'bg-violet-100 text-violet-700'
                            : 'bg-slate-100 text-slate-500'
                          }`}>
                            {freq === 'once' ? t('reminder.one_time') : freq === 'weekly' ? t('reminder.weekly') : freq === 'monthly' ? t('reminder.monthly') : t('reminder.yearly')}
                          </span>
                        </div>
                      </div>
                      <div className={`px-3 py-1 rounded-full text-xs font-semibold shrink-0 ${
                        isToday    ? 'bg-slate-900 text-white'
                        : isTomorrow ? (isPersian ? 'bg-orange-100 text-orange-700' : isHijri ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700')
                        : 'bg-slate-100 text-slate-600'
                      }`}>
                        {getRelativeTimeTranslated(reminder.date, reminder.repeatYearly, t, settings.hijriAdjustment, reminder.hijriDate, reminder.calendarType, reminder.persianDate, freq)}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* My Groups */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold tracking-tight text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <Users size={20} className="text-slate-700" />
                {t('home.my_groups')}
              </h2>
              {groups.length > 0 && (
                <button
                  onClick={() => navigate('/groups')}
                  className="text-sm font-semibold text-blue-600 flex items-center gap-1"
                  data-testid="see-all-groups"
                >
                  {t('home.see_all')}
                  <ChevronRight size={16} />
                </button>
              )}
            </div>
            {groups.length === 0 ? (
              <div className="bg-white p-8 rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.04)] text-center">
                <p className="text-slate-500" style={{ fontFamily: 'Figtree, sans-serif' }}>{t('home.no_groups')}</p>
                <button
                  onClick={() => navigate('/groups')}
                  className="mt-4 text-slate-900 font-semibold text-sm"
                  data-testid="create-first-group"
                >
                  {t('home.create_first_group')}
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {displayedGroups.map((group) => (
                  <div
                    key={group.id}
                    onClick={() => navigate(`/groups/${group.id}`)}
                    className="bg-white p-5 rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.04)] border border-slate-50 flex flex-col gap-3 active:scale-[0.98] transition-all cursor-pointer"
                    data-testid="group-card-item"
                  >
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {group.name}
                      </h3>
                      <span className="text-sm text-slate-500">
                        {group.members?.length || group.memberDetails?.length || 0} {t('home.members')}
                      </span>
                    </div>
                    {Array.isArray(group.memberDetails) && group.memberDetails.length > 0 && (
                      <div className="flex -space-x-3">
                        {group.memberDetails.slice(0, 4).map((member, idx) => (
                          <div
                            key={idx}
                            className="w-8 h-8 rounded-full border-2 border-white bg-slate-200 flex items-center justify-center text-xs font-semibold text-slate-600"
                          >
                            {member.name?.charAt(0)?.toUpperCase() || 'U'}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      </div>

      <BottomNav />

      {/* ── Push Notification Modal ────────────────────────────────── */}
      <AnimatePresence>
        {showPushBanner && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 flex items-center justify-center px-6"
            style={{ zIndex: 9998, backgroundColor: 'rgba(0,0,0,0.5)' }}
            onClick={dismissPushBanner}
          >
            <motion.div
              initial={{ scale: 0.85, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.85, opacity: 0, y: 20 }}
              transition={{ type: 'spring', stiffness: 320, damping: 28 }}
              className="w-full max-w-sm bg-slate-900 text-white rounded-3xl p-6 shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex flex-col items-center text-center gap-4">
                <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center">
                  <Bell size={30} className="text-white" />
                </div>
                <div>
                  <p className="text-lg font-bold mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>{t('push.banner_title')}</p>
                  <p className="text-sm text-white/70" style={{ fontFamily: 'Figtree, sans-serif' }}>{t('push.banner_body')}</p>
                </div>
                <div className="flex gap-3 w-full mt-1">
                  <button onClick={dismissPushBanner} data-testid="push-banner-dismiss"
                    className="flex-1 bg-white/10 hover:bg-white/20 text-white text-sm font-semibold py-3 rounded-2xl transition-colors">
                    {t('push.dismiss')}
                  </button>
                  <button onClick={handleEnablePush} disabled={pushLoading} data-testid="push-banner-allow"
                    className="flex-1 bg-white text-slate-900 text-sm font-semibold py-3 rounded-2xl active:scale-95 transition-all disabled:opacity-60">
                    {pushLoading ? '...' : t('push.allow')}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Hijri Auto-Adjust Toast ────────────────────────────────── */}
      <AnimatePresence>
        {showHijriToast && (
          <motion.div
            initial={{ y: -60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -60, opacity: 0 }}
            className="fixed top-4 left-1/2 -translate-x-1/2 w-full max-w-sm px-4"
            style={{ zIndex: 9999 }}
          >
            <div className="bg-emerald-600 text-white rounded-2xl px-4 py-3 shadow-xl flex items-center gap-2.5">
              <Moon size={16} className="shrink-0" />
              <p className="text-sm font-medium" style={{ fontFamily: 'Figtree, sans-serif' }}>{t('push.hijri_auto')}</p>
              <button onClick={() => setShowHijriToast(false)} className="ml-auto text-white/70 hover:text-white">
                <X size={14} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default HomePage;
